import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { db } from '../firebase';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { syncQrIndexForOrder } from '../utils/qrIndex';
import { QRCode } from 'react-qr-code';
import Spinner from './Spinner';
import './SuccessPage.css';

/** Misma Cloud Function region que Cart / OrderList */
const CONFIRM_MP_PURCHASE_URL =
  'https://us-central1-alegra-club.cloudfunctions.net/confirmMercadoPagoPurchase';

/**
 * Página de retorno tras pagar con Mercado Pago (Checkout Pro).
 *
 * Solo mostramos QR si el pago está acreditado (`isPaid` en Firestore), típicamente después de
 * `confirmMercadoPagoPurchase` (valida `payment_id` en MP). Misma semilla de QR que el backend.
 */
const SuccessPage = () => {
  const [searchParams] = useSearchParams();
  const [phase, setPhase] = useState('loading');
  const [orderData, setOrderData] = useState(null);
  const [emailMeta, setEmailMeta] = useState(null);
  const [errorDetail, setErrorDetail] = useState('');

  const createSHA256Hash = async (input) => {
    const encoder = new TextEncoder();
    const data = encoder.encode(input);
    const hashBuffer = await crypto.subtle.digest('SHA-256', data);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    return hashArray.map((b) => b.toString(16).padStart(2, '0')).join('');
  };

  useEffect(() => {
    let cancelled = false;

    const fetchOrderData = async () => {
      const rawRef =
        searchParams.get('external_reference') || searchParams.get('external_reference_id');
      const externalReference = rawRef ? decodeURIComponent(rawRef.trim()) : '';

      if (!externalReference) {
        setPhase('missing_ref');
        return;
      }

      const st = (
        searchParams.get('status') ||
        searchParams.get('collection_status') ||
        ''
      ).toLowerCase();
      const urlApproved = ['approved', 'success'].includes(st);
      const urlPending = ['pending', 'in_process'].includes(st);
      const urlRejected = ['rejected', 'failure', 'cancelled', 'canceled'].includes(st);

      const paymentIdRaw =
        searchParams.get('payment_id') || searchParams.get('collection_id') || '';
      const paymentId = paymentIdRaw ? String(paymentIdRaw).trim() : '';

      setPhase('loading');
      setEmailMeta(null);
      setErrorDetail('');

      try {
        if (paymentId) {
          try {
            const r = await fetch(CONFIRM_MP_PURCHASE_URL, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ paymentId })
            });
            const j = await r.json().catch(() => ({}));
            if (!cancelled) {
              if (r.ok && j.ok) {
                setEmailMeta({
                  sent: j.emailSent === true,
                  skipped: j.skipped || null
                });
              } else if (r.status === 400 && j.error === 'payment_not_approved') {
                setEmailMeta({ sent: false, skipped: 'payment_not_approved' });
              } else {
                setEmailMeta({ sent: false, skipped: 'confirm_failed' });
              }
            }
          } catch (e) {
            console.warn('confirmMercadoPagoPurchase:', e);
            if (!cancelled) setEmailMeta({ sent: false, skipped: 'network' });
          }
        }

        const orderRef = doc(db, 'compras_mercadopago', externalReference);
        let orderDoc = await getDoc(orderRef);

        let polls = 0;
        while (
          !cancelled &&
          orderDoc.exists() &&
          orderDoc.data().isPaid !== true &&
          (urlApproved || paymentId) &&
          polls < 12
        ) {
          await new Promise((resolve) => setTimeout(resolve, 800));
          orderDoc = await getDoc(orderRef);
          polls += 1;
        }

        if (!orderDoc.exists()) {
          setPhase('not_found');
          return;
        }

        let data = orderDoc.data();

        if (data.isPaid !== true) {
          if (urlRejected) {
            setPhase('rejected');
          } else if (urlPending || st === '') {
            setPhase('pending');
          } else {
            setPhase('unpaid');
          }
          setOrderData(null);
          return;
        }

        const needsQr = (data.cartItems || []).some((item) => !item.qrValue);
        let finalCart = data.cartItems || [];

        if (needsQr) {
          finalCart = await Promise.all(
            (data.cartItems || []).map(async (item) => {
              if (item.qrValue) return item;
              const sha256Hash = await createSHA256Hash(`${item.id}-${data.orderNumber}`);
              return { ...item, qrValue: sha256Hash };
            })
          );
          try {
            await updateDoc(orderRef, { cartItems: finalCart });
          } catch (e) {
            console.warn('Guardar QR en la orden (puede ser reglas; el servidor ya lo hizo):', e);
          }
        }

        try {
          await syncQrIndexForOrder(db, 'compras_mercadopago', externalReference, finalCart);
        } catch (e) {
          console.error('syncQrIndexForOrder:', e);
        }

        if (!cancelled) {
          setOrderData({ ...data, cartItems: finalCart });
          setPhase('success');
        }
      } catch (error) {
        console.error('Error en página de éxito:', error);
        if (!cancelled) {
          setPhase('error');
          setErrorDetail(error.message || 'Error desconocido');
        }
      }
    };

    fetchOrderData();
    return () => {
      cancelled = true;
    };
  }, [searchParams]);

  const emailNotice = () => {
    if (!emailMeta) return null;
    if (emailMeta.sent) {
      return (
        <p className="success-page__notice success-page__notice--ok">
          Te enviamos los <strong>mismos códigos QR</strong> por correo (revisá también la carpeta de spam).
        </p>
      );
    }
    if (emailMeta.skipped === 'already_sent') {
      return (
        <p className="success-page__notice success-page__notice--ok">
          El correo con tus QR ya había sido enviado antes. Si no lo encontrás, revisá spam o contactanos.
        </p>
      );
    }
    return (
      <p className="success-page__notice success-page__notice--warn">
        Si no recibís el correo con los QR en unos minutos, revisá spam o escribinos desde la web. Podés usar la
        captura de pantalla como respaldo.
      </p>
    );
  };

  return (
    <main className="cyber-shell cyber-shell--narrow cyber-shell--flush-top success-page">
      <div className="cyber-shell__frame" aria-hidden="true" />
      <div className="cyber-shell__body success-page__body">
        {phase === 'loading' && (
          <section className="success-page__state success-page__state--loading" role="status" aria-live="polite">
            <Spinner />
            <p className="success-page__eyebrow">Mercado Pago</p>
            <h1 className="cyber-shell__title">Confirmando compra</h1>
            <p className="success-page__lead">Estamos validando tu pago y preparando tus accesos.</p>
          </section>
        )}

        {phase === 'missing_ref' && (
          <section className="success-page__state success-page__state--warning">
            <span className="success-page__badge">Comprobante faltante</span>
            <h1 className="cyber-shell__title">Falta el comprobante</h1>
            <p className="success-page__lead">
              No encontramos el identificador de tu compra en el enlace. Volvé al carrito o contactá soporte.
            </p>
          </section>
        )}

        {phase === 'not_found' && (
          <section className="success-page__state success-page__state--warning">
            <span className="success-page__badge">Orden no encontrada</span>
            <h1 className="cyber-shell__title">No encontramos la orden</h1>
            <p className="success-page__lead">El código de compra no coincide con ningún pedido registrado.</p>
          </section>
        )}

        {phase === 'pending' && (
          <section className="success-page__state success-page__state--pending">
            <span className="success-page__badge">Pago en proceso</span>
            <h1 className="cyber-shell__title">Pago pendiente</h1>
            <p className="success-page__lead">
              Mercado Pago aún está procesando el cobro. Cuando se acredite vas a recibir el correo con los QR y
              podés volver a esta página desde el enlace del mail o desde &quot;Mis compras&quot; si lo agregamos.
            </p>
          </section>
        )}

        {phase === 'unpaid' && (
          <section className="success-page__state success-page__state--warning">
            <span className="success-page__badge">Validación pendiente</span>
            <h1 className="cyber-shell__title">Pago no confirmado</h1>
            <p className="success-page__lead">
              No pudimos verificar un pago acreditado para esta compra. Si ya pagaste, esperá unos minutos o
              contactanos con el número de orden.
            </p>
          </section>
        )}

        {phase === 'rejected' && (
          <section className="success-page__state success-page__state--warning">
            <span className="success-page__badge">Operación rechazada</span>
            <h1 className="cyber-shell__title">Pago no aprobado</h1>
            <p className="success-page__lead">El medio de pago no fue aprobado. Podés intentar de nuevo desde el carrito.</p>
          </section>
        )}

        {phase === 'error' && (
          <section className="success-page__state success-page__state--warning">
            <span className="success-page__badge">Error de validación</span>
            <h1 className="cyber-shell__title">Algo salió mal</h1>
            <p className="success-page__lead">{errorDetail || 'Volvé a intentar más tarde o contactá soporte.'}</p>
          </section>
        )}

        {phase === 'success' && orderData && (
          <>
            <section className="success-page__state success-page__state--success">
              <span className="success-page__badge">Compra acreditada</span>
              <h1 className="cyber-shell__title">Pago acreditado</h1>
              <p className="success-page__lead">
                Tu compra quedó confirmada. Los códigos de abajo son tus accesos: hacé una <strong>captura de
                pantalla</strong> o guardalas, por si no hay señal en la entrada.
              </p>
              {emailNotice()}
            </section>

            <section className="success-page__tickets" aria-label="Códigos QR de acceso">
              {(orderData.cartItems || []).map((item) => (
                <article key={item.id} className="success-page__ticket">
                  <div className="success-page__qr-frame">
                    <QRCode className="success-page__qr" value={item.qrValue} size={256} />
                  </div>
                  <div className="success-page__ticket-info">
                    <p className="success-page__ticket-title">{item.model}</p>
                    <p className="success-page__ticket-meta">Cantidad: {item.quantity}</p>
                  </div>
                </article>
              ))}
            </section>
          </>
        )}
      </div>
    </main>
  );
};

export default SuccessPage;
